/* tslint:disable no-empty */
import { GenericEvent, MethodMeta, ServiceErrorType, ServiceMeta } from '@central-products/transport-bus-common';

import { addListener, getListeners, removeListener } from './bindings/eventListeners';
import { bindService, getServiceBinding, unbindService } from './bindings/serviceBindings';
import {
  getBoundToAnotherInstanceErrorMsg,
  getNotInterfaceErrorMsg,
  getUnknownServiceErrorMsg,
  getUnknownServiceMethodErrorMsg,
} from './errorMessages';
import { callToValidRequest, responseToValidResult } from './utils';

export abstract class AbstractTransport {
  // tslint:disable-next-line:callable-types
  public get<T>(ServiceClass: { new (): any }): T {
    const meta = ServiceMeta.getOrThrow(ServiceClass.prototype, getNotInterfaceErrorMsg(ServiceClass.name));
    const service = new ServiceClass();
    meta.validate(service);

    const { domain, serviceName } = meta;
    meta.methods.forEach((m: MethodMeta) => {
      service[m.property] = (...args: any[]) => {
        const methodArgs = callToValidRequest(m, domain, serviceName, args);
        return this.callMethod(methodArgs).then((data: any) => responseToValidResult(m, domain, serviceName, data));
      };
    });

    meta.events.forEach((e: MethodMeta) => {
      service[e.property] = (callback: (payload: object) => void) => this.addEventListener(meta, e, callback);
    });

    return service as T;
  }

  public bind(service: object): void {
    const meta = ServiceMeta.getOrThrow(service, getNotInterfaceErrorMsg(service.constructor.name));
    meta.validate(service);

    bindService({ implementation: service, ...meta });
    this.bindEvents(service, meta);
    this.onBind(meta);
  }

  public unbind(intfImpl: object) {
    const meta = ServiceMeta.getOrThrow(intfImpl, getNotInterfaceErrorMsg(intfImpl.constructor.name));
    const binding = getServiceBinding(meta);

    if (binding) {
      const { implementation } = binding;
      if (implementation && implementation !== intfImpl) {
        throw new Error(getBoundToAnotherInstanceErrorMsg(meta.serviceName));
      }

      unbindService(meta);
      this.onUnbind(meta);
    }
  }

  protected abstract onBind(meta: ServiceMeta): void;

  protected abstract onUnbind(meta: ServiceMeta): void;

  protected abstract onEventListenerAdded(domain: string, serviceName: string, name: string): void;

  protected abstract onEventListenerRemoved(domain: string, serviceName: string, name: string): void;

  protected notifySubscribers(event: GenericEvent): void {
    const { domain, serviceName, name, payload } = event;
    const eventListeners = getListeners({ domain, serviceName, eventName: name });

    eventListeners.forEach((callback) => {
      try {
        callback(payload);
      } catch (e) {
        console.error(e);
      }
    });
  }

  protected callMethod(data: GenericEvent): Promise<object> {
    const { domain, serviceName, name } = data;
    const binding = getServiceBinding({ domain, serviceName });

    if (!binding || !binding.implementation) {
      return Promise.reject({
        ...data,
        payload: {
          error: ServiceErrorType.ServiceUnknown,
          message: getUnknownServiceErrorMsg(serviceName),
        },
      });
    }

    const method = binding.methods.get(name);
    if (!method) {
      return Promise.reject({
        ...data,
        payload: {
          error: ServiceErrorType.ServiceUnknown,
          message: getUnknownServiceMethodErrorMsg(serviceName, name),
        },
      });
    }

    return method(data);
  }

  private bindEvents(intfImpl: any, meta: ServiceMeta): void {
    const { domain, serviceName, events } = meta;
    events.forEach(({ name, property }) => {
      intfImpl[property]((newState: any) => {
        this.notifySubscribers({ domain, serviceName, name, payload: newState });
      });
    });
  }

  private addEventListener(
    serviceMeta: ServiceMeta,
    eventMeta: MethodMeta,
    callback: (payload: object) => void
  ): () => any {
    const { domain, serviceName } = serviceMeta;
    const { name } = eventMeta;
    const eventListenerData = {
      domain,
      serviceName,
      eventName: name,
      listener: callback,
    };

    addListener(eventListenerData);
    this.onEventListenerAdded(domain, serviceName, name);

    return () => {
      removeListener(eventListenerData);
      this.onEventListenerRemoved(domain, serviceName, name);
    };
  }
}
